import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import forms from "../constants/forms";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import IQuestion from "../constants/forms/interfaces/IQuestion";
import { useSideBar } from "./SideBarContext";

export type TFormDataType = string | boolean | any[] | number | null;

export interface IDataForm {
  [id: string]: TFormDataType;
}

interface IFinalizeFormParams {
  apiRoute?: string;
  appRoute?: string;
  callback: (data: any) => any;
  quizName?: string;
}

export interface IFormContextData {
  form: string;
  setForm: React.Dispatch<React.SetStateAction<string>>;
  formQuestions: any;
  currentQuestion: number;
  setCurrentQuestion: React.Dispatch<React.SetStateAction<number>>;
  nextQuestion: any;
  prevQuestion: any;
  lastQuestionNumber: number;
  data: IDataForm;
  setData: (obj: Object, patch?: boolean) => any;
  setDependentId: (data: string) => any;
  dependentId: string;
  setUsername: (data: string) => any;
  username: string;
  handleFinalizeForm: (data: IFinalizeFormParams) => void;
  handleUnfinishedForm: (data: IFinalizeFormParams) => void;
  filledUntil: number;
  resetContext: () => void;
  quiz: any;
  loadingQuiz: boolean;
}

const notfilledUntilForms = [
  "identificacao",
  "bensimoveis",
  "bensautomoveis",
  "empresarios",
  "publiconboarding",
  "modulesSelector",
];

const offlineForms = ["publiconboarding"];

const FormContext = createContext<IFormContextData>({} as IFormContextData);

const FormProvider: React.FC = ({ children }) => {
  const [form, setForm] = useState("");
  const [formQuestions, setFormQuestions] = useState<IQuestion[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [dependentId, setDependentId] = useState<string>("");
  const [username, setUsername] = useState<string>("");
  const [data, setData] = useState<IDataForm>({});
  const [quiz, setQuiz] = useState<any>({ _id: "", completed: false });
  const [loadingQuiz, setLoadingQuiz] = useState(false);

  const navigate = useNavigate();
  const { updateModules } = useSideBar();

  /* useEffect(() => {
    if (!signed && offlineForms.includes(form) && localStorageValue) {
      setData(localStorageValue as IDataForm);
    }
  }, [signed, form, localStorageValue]);*/

  useEffect(() => {
    if (form) {
      setFormQuestions(forms[form](data) || []);
      if (!offlineForms.includes(form)) {
        setLoadingQuiz(true);
        api
          .get("/quiz", {
            params: {
              name: form,
              ...(dependentId ? { dependentId } : {}),
            },
          })
          .then((res) => {
            setData(res.data.data || {});
            setQuiz(res.data);
            setLoadingQuiz(false);
          });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentId, form]);

  useEffect(() => {
    if (
      form &&
      ((data.quantidade !== null && data.quantidade !== undefined) ||
        data.toAdd === true ||
        data.toAdd === false)
    ) {
      setFormQuestions(forms[form](data) || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.quantidade, form, data.toAdd]);

  const handleChangeData = useCallback(
    ({ id, value, customData, from }, patch = true) => {
      const valueData = customData ? customData : { [id]: value };

      setData((current) => {
        const newData = {
          ...current,
          ...valueData,
        };
        /* if (!signed && offlineForms.includes(form)) {
           setLocalStorageValue(newData);
         }*/
        return newData;
      });

      if (patch && quiz._id) {
        api.patch(`/quiz/${quiz._id}`, valueData, {
          params: {
            ...(dependentId ? { dependentId } : {}),
          },
        });
      }
    },
    [dependentId, quiz]
  );

  const resetContext = useCallback(() => {
    setForm("");
    setFormQuestions([]);
    setCurrentQuestion(0);
    setData({});
    setQuiz({ _id: "", completed: false });
  }, []);

  const nav = useCallback(
    (target: number) => {
      const ruledQuestions = formQuestions.filter((question: any) =>
        question.rule ? question.rule(data, dependentId) : true
      );
      const index = formQuestions.findIndex(
        (q) =>
          ruledQuestions[target].label === q.label &&
          ruledQuestions[target].id === q.id
      );
      setCurrentQuestion(index < 0 ? 0 : index);
      const element = document.getElementById(
        index < 0 ? "question-0" : `question-${target}`
      );
      element?.scrollIntoView({ behavior: "smooth" });
    },
    [formQuestions, data, dependentId]
  );

  const nextQuestion = useCallback(
    (step) => {
      if (formQuestions?.[currentQuestion]?.saveOnNext) {
        handleChangeData({
          id: formQuestions[currentQuestion].id,
          value: data[formQuestions[currentQuestion].id],
        });
      }
      nav(step !== undefined && step !== null ? step : currentQuestion + 1);
    },
    [formQuestions, currentQuestion, data, handleChangeData, nav]
  );

  const prevQuestion = useCallback(
    (step?: number) => {
      nav(step !== undefined && step !== null ? step : currentQuestion - 1);
    },
    [currentQuestion, nav]
  );

  const handleFinalizeForm = useCallback(
    ({ apiRoute, appRoute, callback }) => {
      if (apiRoute) {
        return api
          .post(apiRoute, { completed: true })
          .then((res) => {
            callback(res.data);
            setQuiz({ ...quiz, data, completed: true });
            if (appRoute) {
              navigate(appRoute);
            }
          })
          .catch((err) => {
            console.log(err);
          });
      }
      if (appRoute) {
        navigate(appRoute);
      }
    },
    [navigate, quiz, data]
  );

  const handleUnfinishedForm = useCallback(
    ({ apiRoute, callback, quizName }) => {
      setQuiz({ ...quiz, completed: false });
      api
        .post(apiRoute, { completed: false })
        .then((res) => {
          callback(res.data);
          updateModules({
            name: form || String(quizName).toLowerCase(),
            completed: false,
          });
        })
        .catch((err) => {
          console.log(err);
          setQuiz({ ...quiz, completed: false });
        });
    },
    [quiz, form, updateModules]
  );

  const filledUntil = useMemo(() => {
    return (formQuestions || [])
      .filter((question) => !question.endStep)
      .reduce((pv, question, index) => {
        if (typeof data[question.id] === "boolean" || data[question.id]) {
          return index;
        }
        return pv;
      }, -1);
  }, [data, formQuestions]);

  useEffect(() => {
    if (filledUntil < 0 || notfilledUntilForms.includes(form)) {
      return;
    }
    setCurrentQuestion(filledUntil);
    const element = document.getElementById(`question-${filledUntil}`);
    element?.scrollIntoView({ behavior: "smooth" });
  }, [filledUntil, quiz._id, form]);

  const values = useMemo(
    (): IFormContextData => ({
      form,
      setForm,
      formQuestions,
      currentQuestion,
      setCurrentQuestion,
      nextQuestion,
      prevQuestion,
      lastQuestionNumber:
        formQuestions.filter(
          (q) => !!q.type && (q.rule ? q.rule(data, dependentId) : true)
        ).length - 1,
      data,
      setData: handleChangeData,
      filledUntil,
      handleFinalizeForm,
      handleUnfinishedForm,
      dependentId,
      setDependentId,
      setUsername,
      username,
      resetContext,
      quiz,
      loadingQuiz,
    }),
    [
      form,
      formQuestions,
      currentQuestion,
      nextQuestion,
      prevQuestion,
      data,
      handleChangeData,
      filledUntil,
      handleFinalizeForm,
      handleUnfinishedForm,
      dependentId,
      username,
      resetContext,
      quiz,
      loadingQuiz,
    ]
  );

  return <FormContext.Provider value={values}>{children}</FormContext.Provider>;
};

export const useForm = (): IFormContextData => useContext(FormContext);

export default FormProvider;
